import { gql, NetworkStatus, useLazyQuery, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistCustomerConnectionFragmentDoc,
  ArtistCustomerDetailFragmentDoc,
  ArtistExportCustomersCsvQuery,
  ArtistExportCustomersCsvQueryVariables,
  ConsoleUsersQuery,
  ConsoleUsersQueryVariables,
  FetchNewConsoleUsersQuery,
  FetchNewConsoleUsersQueryVariables,
  OrderBy,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { useAnalytics } from '@/lib/analytics'
import { useDownloadFile } from '@/lib/downloadFile'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleUsers')
  const { user } = useCurrentUser()
  const { analytics } = useAnalytics()
  const { showError, showMessage } = useMessage()
  const { downloadCsv } = useDownloadFile()
  const params = new URLSearchParams(window.location.search)
  const sort = params.get('sort') === OrderBy.Asc ? OrderBy.Asc : OrderBy.Desc
  const [filter, setFilter] = useState<{
    orderBy: OrderBy
    searchTerm: string
  }>({
    orderBy: sort,
    searchTerm: '',
  })

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewConsoleUsersQuery,
    FetchNewConsoleUsersQueryVariables
  >({
    query: FETCH_NEW_USERS,
    variables: {
      orderBy: filter.orderBy,
      searchTerm: '',
      pageInput: { limit: 20 },
    },
    fields: [
      {
        fragmentName: 'ArtistCustomerDetail',
        fieldName: 'artistCustomers',
        fragment: ArtistCustomerDetailFragmentDoc,
      },
    ],
  })

  const { data, loading, fetchMore, refetch, networkStatus } = useQuery<
    ConsoleUsersQuery,
    ConsoleUsersQueryVariables
  >(QUERY, {
    variables: {
      ...filter,
      pageInput: { limit: 20 },
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  const [artistExportCustomersCsv, { loading: downloadingCsv }] = useLazyQuery<
    ArtistExportCustomersCsvQuery,
    ArtistExportCustomersCsvQueryVariables
  >(ARTIST_EXPORT_CUSTOMERS_CSV, {
    async onCompleted({ artistExportCustomersCsv }) {
      if (!artistExportCustomersCsv?.csvUrl) {
        return showError(new Error(t('downloadCsvError')))
      }
      await downloadCsv(artistExportCustomersCsv?.csvUrl || '').then(() => {
        showMessage(
          t('downloadCsvSuccess', 'ダウンロードが完了しました'),
          'success'
        )
      })
    },
  })

  useEffect(() => {
    if (loading || !!data?.artistCustomers.edges.length) return

    analytics?.track('impression_noData', {
      applicationKey: config.applicationKey,
      artistId: user?.uid,
      name: 'customerSearch',
    })
  }, [loading])

  const isNextFetch = !!data

  return {
    state: {
      isFirstLoading:
        networkStatus === NetworkStatus.loading && isFirstLoadRef.current,
      loading: networkStatus === NetworkStatus.loading && !isNextFetch,
      refresh:
        networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.refetch ||
        networkStatus === NetworkStatus.fetchMore,
      users: data?.artistCustomers ?? null,
      downloadingCsv,
      haveSearchTermValue: !!filter.searchTerm,
      hasNextPage: !!data?.artistCustomers?.pageInfo?.hasNextPage,
      artistId: user?.uid ?? '',
      user,
      artist: data?.artist ?? null,
      sort: filter.orderBy,
      membership: data?.artist?.subscription?.products ?? undefined,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            ...filter,
            pageInput: {
              cursor: data?.artistCustomers.pageInfo.endCursor,
              limit: 20,
            },
          },
        })
      },
      async onSubmit(values) {
        setFilter(values)
        await refetch(values)
      },
      async onDownloadCsv() {
        await artistExportCustomersCsv()
      },
    },
  }
})

const QUERY = gql`
  query ConsoleUsers(
    $orderBy: OrderBy
    $pageInput: PageInput
    $searchTerm: String
  ) {
    artistCustomers(
      orderBy: $orderBy
      pageInput: $pageInput
      searchTerm: $searchTerm
    ) {
      ...ArtistCustomerConnection
    }
    artist {
      id
      paymentType
      hasArrangingDeliveryItems
      subscription {
        products {
          productId
        }
      }
    }
  }
  ${ArtistCustomerConnectionFragmentDoc}
`

const FETCH_NEW_USERS = gql`
  query FetchNewConsoleUsers(
    $orderBy: OrderBy
    $pageInput: PageInput
    $searchTerm: String
  ) {
    artistCustomers(
      orderBy: $orderBy
      pageInput: $pageInput
      searchTerm: $searchTerm
    ) {
      ...ArtistCustomerConnection
    }
  }
  ${ArtistCustomerConnectionFragmentDoc}
`

const ARTIST_EXPORT_CUSTOMERS_CSV = gql`
  query ArtistExportCustomersCsv {
    artistExportCustomersCsv {
      csvUrl
    }
  }
`
